import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8081";

interface ChatAgent {
  id: string;
  name: string;
  description?: string;
  business_domain?: string;
}

interface ChatAgentVersion {
  id: string;
  version: number;
  stage: string;
}

interface EntityAssignment {
  id: string;
  entity_id: string;
  entity_name: string;
  entity_phone: string;
  chat_agent_id: string;
  agent_name: string;
  chat_agent_version_id?: string;
  version_number?: number;
  version_stage?: string;
  is_default: boolean;
  enabled?: boolean; // Whether the agent is active
  created_at: string;
  agent_schedule?: boolean[]; // 24-element array, one for each hour of day (legacy)
  agent_schedule_weekly?: boolean[][]; // 7x24 array: [day][hour] where day 0=Sun,1=Mon,...,6=Sat
  entity_timezone?: string;
}

interface Entity {
  id: string;
  name: string;
  phone: string;
}

interface LeadStatusRoutingRule {
  id: string;
  tenant_id: string;
  entity_id: string;
  lead_status: string;
  chat_agent_id: string;
  agent_name: string;
  chat_agent_version_id?: string;
  version_number?: number;
  version_stage?: string;
  priority: number;
  enabled: boolean;
  description?: string;
  created_at: string;
}

interface EntityAssignmentsProps {
  tenantId: string;
  selectedAdminEntityId?: string | null;
}

export const EntityAssignments: React.FC<EntityAssignmentsProps> = ({
  tenantId,
  selectedAdminEntityId
}) => {
  const [assignments, setAssignments] = useState<EntityAssignment[]>([]);
  const [agents, setAgents] = useState<ChatAgent[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>('');
  const [selectedEntityName, setSelectedEntityName] = useState<string>('');
  const [selectedEntityTimezone, setSelectedEntityTimezone] = useState<string>('');

  // Assignment form state
  const [formData, setFormData] = useState({
    agentId: '',
    versionId: '' // Empty means use production version
  });

  // Schedule state - weekly schedule (7 days x 24 hours)
  const [weeklySchedule, setWeeklySchedule] = useState<boolean[][]>(
    Array.from({ length: 7 }, () => new Array(24).fill(false))
  );

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayShortNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const [availableVersions, setAvailableVersions] = useState<ChatAgentVersion[]>([]);

  // Lead Status Routing state
  const [leadStatusRules, setLeadStatusRules] = useState<LeadStatusRoutingRule[]>([]);
  const [leadStatusRoutingEnabled, setLeadStatusRoutingEnabled] = useState(false);
  const [selectedEntityForRouting, setSelectedEntityForRouting] = useState<string>('');
  const [showLeadStatusModal, setShowLeadStatusModal] = useState(false);
  const [editingRule, setEditingRule] = useState<LeadStatusRoutingRule | null>(null);
  const [leadStatusForm, setLeadStatusForm] = useState({
    leadStatus: '',
    chatAgentId: '',
    priority: 100,
    description: ''
  });
  const [loadingRules, setLoadingRules] = useState(false);
  const [availableLeadStatuses, setAvailableLeadStatuses] = useState<string[]>([]);

  // Zoho CRM lead statuses (exact match required)
  // Note: "None" is for leads where status hasn't been set yet
  const commonLeadStatuses = [
    'None',
    'Attempted to Contact',
    'Attempted to Contact (1x)',
    'Attempted to Contact (2x)',
    'Contacted - Low (1)',
    'Contacted - Mid (2)',
    'Contacted - High (3)',
    'Appt Fixed (4)',
    'Contact in Future',
    'Junk Lead',
    'Lost Lead',
    'Lost Lead (Qualified)',
    'Closed Deal',
    '*' // Wildcard/default (fallback for all other cases)
  ];
  const leadStatusOptions = Array.from(new Set([...commonLeadStatuses, ...availableLeadStatuses]));

  // ESC key handler for closing modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showAssignModal) {
          setShowAssignModal(false);
          setSelectedEntityId('');
          setFormData({ agentId: '', versionId: '' });
        } else if (showScheduleModal) {
          setShowScheduleModal(false);
        } else if (showLeadStatusModal) {
          setShowLeadStatusModal(false);
          setEditingRule(null);
          setLeadStatusForm({ leadStatus: '', chatAgentId: '', priority: 100, description: '' });
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showAssignModal, showScheduleModal, showLeadStatusModal]);

  useEffect(() => {
    loadAssignments();
    loadAgents();
    loadEntities();
  }, [tenantId]);

  useEffect(() => {
    if (formData.agentId) {
      loadVersionsForAgent(formData.agentId);
    }
  }, [formData.agentId]);

  useEffect(() => {
    if (!selectedAdminEntityId) {
      return;
    }

    setSelectedEntityForRouting((currentEntityId) => {
      if (currentEntityId === selectedAdminEntityId) {
        return currentEntityId;
      }

      return selectedAdminEntityId;
    });

    loadLeadStatusRules(selectedAdminEntityId);
    loadAvailableLeadStatuses(selectedAdminEntityId);
  }, [selectedAdminEntityId, tenantId]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entity-assignments?tenantId=${tenantId}`, { headers: authHeaders });
      const data = await response.json();

      if (data.ok) {
        setAssignments(data.assignments);
      } else {
        setError(data.error || 'Failed to load assignments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/chat-agents?tenantId=${tenantId}`, { headers: authHeaders });
      const data = await response.json();

      if (data.ok) {
        setAgents(data.agents);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadEntities = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities?tenantId=${tenantId}`, { headers: authHeaders });
      const data = await response.json();

      if (data.ok) {
        setEntities(data.entities || []);
      }
    } catch (err) {
      console.error('Failed to load entities:', err);
    }
  };

  const loadVersionsForAgent = async (agentId: string) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/chat-agents/${agentId}/versions?tenantId=${tenantId}`, { headers: authHeaders });
      const data = await response.json();

      if (data.ok) {
        setAvailableVersions(data.versions);
      }
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  // Load lead status routing rules for an entity
  const loadLeadStatusRules = async (entityId: string) => {
    if (!entityId) return;

    try {
      setLoadingRules(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/api/lead-status-routing?tenantId=${tenantId}&entityId=${entityId}`,
        { headers: authHeaders }
      );
      const data = await response.json();

      if (data.ok) {
        setLeadStatusRules(data.rules || []);
        setLeadStatusRoutingEnabled(data.useLeadStatusRouting || false);
      }
    } catch (err) {
      console.error('Failed to load lead status routing rules:', err);
    } finally {
      setLoadingRules(false);
    }
  };

  const loadAvailableLeadStatuses = async (entityId?: string) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const params = new URLSearchParams({ tenantId });
      if (entityId) {
        params.set('entityId', entityId);
      }
      const query = `?${params.toString()}`;
      const response = await fetch(`${API_BASE}/api/admin/zoho/lead-statuses${query}`, { headers: authHeaders });
      const data = await response.json();

      if (data.ok) {
        setAvailableLeadStatuses(data.lead_statuses || []);
      }
    } catch (err) {
      console.error('Failed to load available lead statuses:', err);
    }
  };

  // Toggle lead status routing for an entity
  const handleToggleLeadStatusRouting = async (assignmentId: string, _entityId: string, enabled: boolean) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entity-assignments/${assignmentId}/lead-status-routing`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, enabled })
      });

      const data = await response.json();

      if (data.ok) {
        setLeadStatusRoutingEnabled(enabled);
        await loadAssignments();
      } else {
        setError(data.error || 'Failed to toggle lead status routing');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle lead status routing');
    }
  };

  // Create or update a lead status routing rule
  const handleSaveLeadStatusRule = async () => {
    if (!selectedEntityForRouting || !leadStatusForm.leadStatus || !leadStatusForm.chatAgentId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();

      if (editingRule) {
        // Update existing rule
        const response = await fetch(`${API_BASE}/api/lead-status-routing/${editingRule.id}`, {
          method: 'PUT',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            leadStatus: leadStatusForm.leadStatus,
            chatAgentId: leadStatusForm.chatAgentId,
            priority: leadStatusForm.priority,
            description: leadStatusForm.description
          })
        });

        const data = await response.json();

        if (!data.ok) {
          setError(data.error || 'Failed to update routing rule');
          return;
        }
      } else {
        // Create new rule
        const response = await fetch(`${API_BASE}/api/lead-status-routing`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            entityId: selectedEntityForRouting,
            leadStatus: leadStatusForm.leadStatus,
            chatAgentId: leadStatusForm.chatAgentId,
            priority: leadStatusForm.priority,
            description: leadStatusForm.description
          })
        });

        const data = await response.json();

        if (!data.ok) {
          setError(data.error || 'Failed to create routing rule');
          return;
        }
      }

      // Reload rules and close modal
      await loadLeadStatusRules(selectedEntityForRouting);
      setShowLeadStatusModal(false);
      setEditingRule(null);
      setLeadStatusForm({ leadStatus: '', chatAgentId: '', priority: 100, description: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to save routing rule');
    }
  };

  // Delete a lead status routing rule
  const handleDeleteLeadStatusRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this routing rule?')) {
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/api/lead-status-routing/${ruleId}?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'DELETE',
          headers: authHeaders
        }
      );

      const data = await response.json();

      if (data.ok) {
        await loadLeadStatusRules(selectedEntityForRouting);
      } else {
        setError(data.error || 'Failed to delete routing rule');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete routing rule');
    }
  };

  // Open modal to add new rule
  const openAddLeadStatusRuleModal = () => {
    setEditingRule(null);
    setLeadStatusForm({ leadStatus: '', chatAgentId: '', priority: 100, description: '' });
    loadAvailableLeadStatuses(selectedEntityForRouting);
    setShowLeadStatusModal(true);
  };

  // Open modal to edit existing rule
  const openEditLeadStatusRuleModal = (rule: LeadStatusRoutingRule) => {
    setEditingRule(rule);
    setLeadStatusForm({
      leadStatus: rule.lead_status,
      chatAgentId: rule.chat_agent_id,
      priority: rule.priority,
      description: rule.description || ''
    });
    loadAvailableLeadStatuses(selectedEntityForRouting);
    setShowLeadStatusModal(true);
  };

  const handleAssignAgent = async () => {
    if (!selectedEntityId || !formData.agentId) {
      setError('Please select an entity and agent');
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${selectedEntityId}/agent`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          agentId: formData.agentId,
          versionId: formData.versionId || null
        })
      });

      const data = await response.json();

      if (data.ok) {
        await loadAssignments();
        setShowAssignModal(false);
        setSelectedEntityId('');
        setFormData({ agentId: '', versionId: '' });
      } else {
        setError(data.error || 'Failed to assign agent');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to assign agent');
    }
  };

  const handleRemoveAssignment = async (entityId: string) => {
    if (!confirm('Are you sure you want to remove this agent assignment? The entity will revert to the default agent.')) {
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entities/${entityId}/agent?tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      const data = await response.json();

      if (data.ok) {
        await loadAssignments();
      } else {
        setError(data.error || 'Failed to remove assignment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove assignment');
    }
  };

  const handleToggleAgent = async (assignment: EntityAssignment) => {
    const newState = !assignment.enabled;
    const action = newState ? 'start' : 'stop';

    if (!confirm(`Are you sure you want to ${action} ${assignment.agent_name} for ${assignment.entity_name}?`)) {
      return;
    }

    try {
      const result = await orchestratorApi.toggleAgentAssignment(assignment.id, tenantId, newState);

      if (result.ok) {
        await loadAssignments();
      } else {
        setError(result.error || `Failed to ${action} agent`);
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${action} agent`);
    }
  };

  const openScheduleModal = (assignment: EntityAssignment) => {
    setSelectedAssignmentId(assignment.id);
    setSelectedEntityName(assignment.entity_name);
    setSelectedEntityTimezone(assignment.entity_timezone || 'Asia/Singapore');

    // Load weekly schedule if available, otherwise create from daily schedule
    if (assignment.agent_schedule_weekly && assignment.agent_schedule_weekly.length === 7) {
      setWeeklySchedule(assignment.agent_schedule_weekly);
    } else {
      // Convert legacy 24-hour schedule to weekly (apply same schedule to all days)
      const dailySchedule = assignment.agent_schedule || new Array(24).fill(true);
      setWeeklySchedule(Array.from({ length: 7 }, () => [...dailySchedule]));
    }
    setShowScheduleModal(true);
  };

  const toggleHour = (dayIndex: number, hourIndex: number) => {
    setWeeklySchedule(prev => {
      const newSchedule = prev.map((day, d) =>
        d === dayIndex
          ? day.map((active, h) => h === hourIndex ? !active : active)
          : [...day]
      );
      return newSchedule;
    });
  };

  const saveSchedule = async () => {
    try {
      // DEBUG: Log the schedule being sent
      console.log('[DEBUG-SAVE] Saving weekly schedule:', {
        tenantId,
        weeklyScheduleLength: weeklySchedule.length,
        weeklySchedule: weeklySchedule,
        selectedAssignmentId
      });

      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/entity-assignments/${selectedAssignmentId}/schedule`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantId,
          weekly_schedule: weeklySchedule
        })
      });

      const data = await response.json();
      console.log('[DEBUG-SAVE] Response:', data);

      if (data.ok) {
        await loadAssignments();
        setShowScheduleModal(false);
      } else {
        setError(data.error || 'Failed to save schedule');
      }
    } catch (err: any) {
      console.log('[DEBUG-SAVE] Error:', err);
      setError(err.message || 'Failed to save schedule');
    }
  };

  const formatScheduleDisplay = (weeklySchedule?: boolean[][], dailySchedule?: boolean[]) => {
    // Use weekly schedule if available, otherwise fall back to daily
    if (weeklySchedule && weeklySchedule.length === 7) {
      // Check if all hours are active (24/7)
      const allActive = weeklySchedule.every(day => day.every(h => h));
      if (allActive) {
        return <span style={{ color: '#10b981', fontWeight: '500' }}>24/7 Active</span>;
      }

      // Check if no hours are active
      const allInactive = weeklySchedule.every(day => day.every(h => !h));
      if (allInactive) {
        return <span style={{ color: '#ef4444', fontWeight: '500' }}>Inactive</span>;
      }

      // Count active hours per day
      const activeHoursPerDay = weeklySchedule.map((day, dayIndex) => ({
        day: dayShortNames[dayIndex],
        active: day.filter(h => h).length
      }));

      const activeDays = activeHoursPerDay.filter(d => d.active > 0);
      if (activeDays.length === 7) {
        return <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>All days active</span>;
      }

      return (
        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {activeDays.map(d => `${d.day}(${d.active}h)`).join(', ')}
        </span>
      );
    }

    // Legacy daily schedule support
    if (!dailySchedule || dailySchedule.every(h => !h)) {
      return <span style={{ color: '#10b981', fontWeight: '500' }}>24/7 Active</span>;
    }

    const activeHours = dailySchedule.map((active, hour) => active ? hour : -1).filter(h => h !== -1);
    if (activeHours.length === 0) {
      return <span style={{ color: '#ef4444', fontWeight: '500' }}>Inactive</span>;
    }

    // Group consecutive hours
    const ranges: string[] = [];
    let start = activeHours[0];
    let prev = activeHours[0];

    for (let i = 1; i < activeHours.length; i++) {
      if (activeHours[i] !== prev + 1) {
        ranges.push(`${start}:00-${prev + 1}:00`);
        start = activeHours[i];
      }
      prev = activeHours[i];
    }
    ranges.push(`${start}:00-${prev + 1}:00`);

    return (
      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
        {ranges.join(', ')}
      </span>
    );
  };

  const openAssignModal = (entityId?: string) => {
    setSelectedEntityId(entityId || '');
    setShowAssignModal(true);
    setFormData({ agentId: '', versionId: '' });
  };

  // Get entities that are not yet assigned
  const unassignedEntities = entities.filter(
    entity => !assignments.some(a => a.entity_id === entity.id)
  );

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'draft': return '#94a3b8';
      case 'production': return '#10b981';
      case 'archived': return '#6b7280';
      default: return '#94a3b8';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading entity assignments...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', background: '#f9fafb', minHeight: '100%' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', color: '#1f2937' }}>
              Entity Agent Assignments
            </h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '1rem' }}>
              Assign specific AI agents to WhatsApp entities (phone numbers)
            </p>
          </div>
          <button
            onClick={() => openAssignModal()}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
            onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
          >
            <span style={{ fontSize: '1.2rem' }}>➕</span>
            Add Assignment
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {/* Assignments Table */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Entity
                </th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Assigned Agent
                </th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Version
                </th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Schedule
                </th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Assigned Date
                </th>
                <th style={{
                  padding: '1rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.95rem'
                  }}>
                    No entity assignments found. Assign agents to entities to get started.
                  </td>
                </tr>
              ) : (
                assignments.map(assignment => (
                  <tr key={assignment.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                          {assignment.entity_name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {assignment.entity_phone}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ fontWeight: '500', color: '#374151' }}>
                          {assignment.agent_name}
                        </div>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          background: assignment.enabled ? '#d1fae5' : '#fee2e2',
                          color: assignment.enabled ? '#065f46' : '#991b1b'
                        }}>
                          {assignment.enabled ? 'Active' : 'Stopped'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {assignment.version_number ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: '500', color: '#374151' }}>
                            v{assignment.version_number}
                          </span>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            background: `${getStageColor(assignment.version_stage || 'draft')}20`,
                            color: getStageColor(assignment.version_stage || 'draft'),
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {assignment.version_stage || 'draft'}
                          </span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                          Auto (Production)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {formatScheduleDisplay(assignment.agent_schedule_weekly, assignment.agent_schedule)}
                        <button
                          onClick={() => openScheduleModal(assignment)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {new Date(assignment.created_at).toLocaleDateString('en-SG', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleToggleAgent(assignment)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: assignment.enabled ? '#10b981' : '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          title={assignment.enabled ? 'Click to stop agent' : 'Click to start agent'}
                        >
                          {assignment.enabled ? 'Stop' : 'Start'}
                        </button>
                        <button
                          onClick={() => openAssignModal(assignment.entity_id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#f97316',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Reassign
                        </button>
                        <button
                          onClick={() => handleRemoveAssignment(assignment.entity_id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Agent Routing Conditions Section */}
        <div style={{
          marginTop: '2rem',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '1.5rem'
        }}>
          <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Agent Routing Conditions
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                Route messages to different agents based on CRM lead status or state, including closed deals
              </p>
            </div>
          </div>

          {/* Entity selector for routing */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              fontSize: '0.9rem',
              color: '#374151'
            }}>
              Select Entity to Configure
            </label>
            <select
              value={selectedEntityForRouting}
              onChange={(e) => {
                setSelectedEntityForRouting(e.target.value);
                if (e.target.value) {
                  loadLeadStatusRules(e.target.value);
                  loadAvailableLeadStatuses(e.target.value);
                } else {
                  setLeadStatusRules([]);
                  setLeadStatusRoutingEnabled(false);
                  setAvailableLeadStatuses([]);
                }
              }}
              style={{
                width: '300px',
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            >
              <option value="">-- Select an entity --</option>
              {assignments.map(assignment => (
                <option key={assignment.entity_id} value={assignment.entity_id}>
                  {assignment.entity_name} ({assignment.entity_phone})
                </option>
              ))}
            </select>
          </div>

          {selectedEntityForRouting && (
            <>
              {/* Enable/Disable Toggle */}
              <div style={{
                padding: '1rem',
                background: leadStatusRoutingEnabled ? '#d1fae5' : '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    Agent Routing Conditions: {leadStatusRoutingEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                    {leadStatusRoutingEnabled
                      ? 'Messages will be routed based on the lead status / state rules below'
                      : 'Enable to route messages to different agents based on CRM lead status or state'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    const assignment = assignments.find(a => a.entity_id === selectedEntityForRouting);
                    if (assignment) {
                      handleToggleLeadStatusRouting(assignment.id, selectedEntityForRouting, !leadStatusRoutingEnabled);
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: leadStatusRoutingEnabled ? '#ef4444' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {leadStatusRoutingEnabled ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Routing Rules Table */}
              {leadStatusRoutingEnabled && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, fontSize: '1rem', color: '#374151' }}>Routing Rules</h4>
                    <button
                      onClick={openAddLeadStatusRuleModal}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      + Add Rule
                    </button>
                  </div>

                  {loadingRules ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                      Loading rules...
                    </div>
                  ) : leadStatusRules.length === 0 ? (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      No routing rules configured. Add rules to route messages based on lead status or state.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                            LEAD STATUS / STATE
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                            ROUTE TO AGENT
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                            PRIORITY
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadStatusRules.map(rule => (
                          <tr key={rule.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '0.75rem' }}>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                background: rule.lead_status === '*' ? '#dbeafe' : '#fef3c7',
                                color: rule.lead_status === '*' ? '#1e40af' : '#92400e',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}>
                                {rule.lead_status === '*' ? '* (Default)' : rule.lead_status}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              <span style={{ fontWeight: '500', color: '#374151' }}>
                                {rule.agent_name}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                {rule.priority}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => openEditLeadStatusRuleModal(rule)}
                                  style={{
                                    padding: '0.4rem 0.8rem',
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteLeadStatusRule(rule.id)}
                                  style={{
                                    padding: '0.4rem 0.8rem',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Help text */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#0369a1'
                  }}>
                    <strong>How it works:</strong>
                    <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                      <li>Rules are matched in priority order (lower number = higher priority)</li>
                      <li>Use "*" as lead status for a default/fallback rule</li>
                      <li>If no rule matches, the entity's default agent is used</li>
                      <li>Lead status is fetched from your CRM (cached for performance)</li>
                    </ul>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assign Agent Modal */}
      {showAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem' }}>
              Assign Agent to Entity
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Select Entity *
              </label>
              <select
                value={selectedEntityId}
                onChange={(e) => setSelectedEntityId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="">-- Select an entity --</option>
                {unassignedEntities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name} ({entity.phone})
                  </option>
                ))}
              </select>
              {unassignedEntities.length === 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                  All entities have been assigned to agents
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Select Agent *
              </label>
              <select
                value={formData.agentId}
                onChange={(e) => setFormData({ ...formData, agentId: e.target.value, versionId: '' })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="">-- Select an agent --</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} {agent.business_domain ? `(${agent.business_domain})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {formData.agentId && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#374151'
                }}>
                  Version (Optional)
                </label>
                <select
                  value={formData.versionId}
                  onChange={(e) => setFormData({ ...formData, versionId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="">Auto (Use Production Version)</option>
                  {availableVersions.map(version => (
                    <option key={version.id} value={version.id}>
                      Version {version.version} - {version.stage.toUpperCase()}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                  Leave as "Auto" to automatically use the production version
                </div>
              </div>
            )}

            {error && (
              <div style={{
                padding: '0.75rem',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedEntityId('');
                  setFormData({ agentId: '', versionId: '' });
                  setError(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignAgent}
                disabled={!formData.agentId}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: formData.agentId ? '#f97316' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: formData.agentId ? 'pointer' : 'not-allowed'
                }}
              >
                Assign Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            width: '900px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>
              📅 Agent Schedule - {selectedEntityName}
            </h3>
            <p style={{ margin: '0 0 0.25rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
              Click hours to toggle when the AI agent should be active (by day of week)
            </p>
            <p style={{ margin: '0 0 1.5rem 0', color: '#10b981', fontSize: '0.85rem', fontWeight: '600' }}>
              Timezone: {selectedEntityTimezone}
            </p>

            {/* Weekly Schedule Grid - 7 Days x 24 Hours */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '60px repeat(24, 1fr)',
                gap: '2px',
                marginBottom: '5px',
                fontSize: '0.7rem',
                color: '#6b7280'
              }}>
                <div></div>
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    {i}
                  </div>
                ))}
              </div>

              {dayNames.map((_dayName, dayIndex) => (
                <div key={dayIndex} style={{ marginBottom: '5px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '60px repeat(24, 1fr)',
                    gap: '2px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textAlign: 'center',
                      color: '#374151',
                      padding: '2px'
                    }}>
                      {dayShortNames[dayIndex]}
                    </div>
                    {Array.from({ length: 24 }, (_, hourIndex) => (
                      <button
                        key={hourIndex}
                        onClick={() => toggleHour(dayIndex, hourIndex)}
                        style={{
                          padding: '8px 2px',
                          background: weeklySchedule[dayIndex]?.[hourIndex] ? '#10b981' : '#f3f4f6',
                          color: weeklySchedule[dayIndex]?.[hourIndex] ? 'white' : '#6b7280',
                          border: `1px solid ${weeklySchedule[dayIndex]?.[hourIndex] ? '#059669' : '#e5e7eb'}`,
                          borderRadius: '3px',
                          fontSize: '0.65rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          textAlign: 'center',
                          minHeight: '24px'
                        }}
                        onMouseOver={(e) => {
                          if (!weeklySchedule[dayIndex]?.[hourIndex]) {
                            e.currentTarget.style.background = '#e5e7eb';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!weeklySchedule[dayIndex]?.[hourIndex]) {
                            e.currentTarget.style.background = '#f3f4f6';
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Presets */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Quick Presets:
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setWeeklySchedule(Array.from({ length: 7 }, () => new Array(24).fill(true)))}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  24/7 (All Days)
                </button>
                <button
                  onClick={() => setWeeklySchedule(Array.from({ length: 7 }, () => new Array(24).fill(false)))}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
                <button
                  onClick={() => {
                    // Weekdays 9am-6pm, Weekends 24/7
                    const businessSchedule = Array.from({ length: 7 }, (_, dayIndex) => {
                      const isWeekend = dayIndex === 0 || dayIndex === 6;
                      if (isWeekend) {
                        return new Array(24).fill(true);
                      } else {
                        const hours = new Array(24).fill(false);
                        for (let i = 9; i < 18; i++) hours[i] = true;
                        return hours;
                      }
                    });
                    setWeeklySchedule(businessSchedule);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Weekdays 9am-6pm, Weekends 24/7
                </button>
                <button
                  onClick={() => {
                    // Business hours Mon-Fri, inactive weekends
                    const weekdaySchedule = Array.from({ length: 7 }, (_, dayIndex) => {
                      const isWeekend = dayIndex === 0 || dayIndex === 6;
                      if (isWeekend) {
                        return new Array(24).fill(false);
                      } else {
                        const hours = new Array(24).fill(false);
                        for (let i = 9; i < 18; i++) hours[i] = true;
                        return hours;
                      }
                    });
                    setWeeklySchedule(weekdaySchedule);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Weekdays 9am-6pm Only
                </button>
                <button
                  onClick={() => {
                    // Weekdays: 6pm-midnight + midnight-9am (off 9am-6pm), Weekends 24/7
                    const afterHoursSchedule = Array.from({ length: 7 }, (_, dayIndex) => {
                      const isWeekend = dayIndex === 0 || dayIndex === 6;
                      if (isWeekend) {
                        return new Array(24).fill(true); // Weekends 24/7
                      } else {
                        // Weekdays: 6pm-midnight (18-23) + midnight-9am (0-8)
                        const hours = new Array(24).fill(false);
                        for (let i = 0; i < 9; i++) hours[i] = true; // Midnight-9am
                        for (let i = 18; i < 24; i++) hours[i] = true; // 6pm-midnight
                        return hours;
                      }
                    });
                    setWeeklySchedule(afterHoursSchedule);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Weekdays After Hours + Weekends 24/7
                </button>
              </div>
            </div>

            {/* Summary */}
            <div style={{
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#6b7280'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <span>Total Active Hours: {weeklySchedule.flat().filter(h => h).length} / 168</span>
                <span>Average per Day: {(weeklySchedule.flat().filter(h => h).length / 7).toFixed(1)} hours</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setWeeklySchedule(Array.from({ length: 7 }, () => new Array(24).fill(false)));
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveSchedule}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Status Routing Rule Modal */}
      {showLeadStatusModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem' }}>
              {editingRule ? 'Edit Routing Rule' : 'Add Routing Rule'}
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Lead Status *
              </label>
              <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Suggested values are loaded from CRM lead statuses currently seen in ForceMX for this entity.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {leadStatusOptions.map(status => (
                  <button
                    key={status}
                    onClick={() => setLeadStatusForm({ ...leadStatusForm, leadStatus: status })}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: leadStatusForm.leadStatus === status ? '#3b82f6' : '#f3f4f6',
                      color: leadStatusForm.leadStatus === status ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {status === '*' ? '* (Default)' : status}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={leadStatusForm.leadStatus}
                onChange={(e) => setLeadStatusForm({ ...leadStatusForm, leadStatus: e.target.value })}
                placeholder="Or enter custom lead status"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Use "*" for a default/fallback rule that matches when no other rule matches
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Route to Agent *
              </label>
              <select
                value={leadStatusForm.chatAgentId}
                onChange={(e) => setLeadStatusForm({ ...leadStatusForm, chatAgentId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="">-- Select an agent --</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} {agent.business_domain ? `(${agent.business_domain})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Priority
              </label>
              <input
                type="number"
                value={leadStatusForm.priority}
                onChange={(e) => setLeadStatusForm({ ...leadStatusForm, priority: parseInt(e.target.value) || 100 })}
                min="1"
                max="1000"
                style={{
                  width: '100px',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Lower number = higher priority. Default is 100.
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={leadStatusForm.description}
                onChange={(e) => setLeadStatusForm({ ...leadStatusForm, description: e.target.value })}
                placeholder="e.g., Route junk leads to acknowledgement agent"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowLeadStatusModal(false);
                  setEditingRule(null);
                  setLeadStatusForm({ leadStatus: '', chatAgentId: '', priority: 100, description: '' });
                  setError(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLeadStatusRule}
                disabled={!leadStatusForm.leadStatus || !leadStatusForm.chatAgentId}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: (leadStatusForm.leadStatus && leadStatusForm.chatAgentId) ? '#10b981' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: (leadStatusForm.leadStatus && leadStatusForm.chatAgentId) ? 'pointer' : 'not-allowed'
                }}
              >
                {editingRule ? 'Update Rule' : 'Add Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
